import { format } from "date-fns";
import { formatDateForInput } from "views/admin/Accounts/accountUtils";

export const createEmptyPaymentDraft = () => ({
  amount: "",
  date: format(new Date(), "yyyy-MM-dd"),
  reference: "",
  notes: "",
  attachments: [],
  entryType: "PAYMENT",
});

export const isRefundEntry = (entryType) =>
  String(entryType || "PAYMENT").toUpperCase() === "REFUND";

export const normalizePaymentEntry = (payment) => ({
  id: payment.id || crypto.randomUUID(),
  amount: payment.amount != null && payment.amount !== "" ? String(payment.amount) : "",
  entryType: isRefundEntry(payment.entryType) ? "REFUND" : "PAYMENT",
  date: payment.date ? formatDateForInput(payment.date) : "",
  reference: payment.reference ?? "",
  notes: payment.notes ?? "",
  attachments: Array.isArray(payment.attachments)
    ? payment.attachments.filter(Boolean)
    : payment.attachment
    ? [payment.attachment]
    : [],
  notifyAccounts: Boolean(payment.notifyAccounts),
  createdAt: payment.createdAt ?? null,
});

export const normalizePaymentsForRow = (row) => {
  if (Array.isArray(row.payments)) {
    return row.payments.map(normalizePaymentEntry);
  }
  const legacyPaid = Number(row.paidAmount);
  if (legacyPaid > 0) {
    return [
      normalizePaymentEntry({
        id: crypto.randomUUID(),
        amount: String(row.paidAmount),
        date: row.date ? formatDateForInput(row.date) : format(new Date(), "yyyy-MM-dd"),
        reference: "",
        notes: "",
        attachments: [],
        notifyAccounts: false,
      }),
    ];
  }
  return [];
};

/**
 * Drafts for the payments modal built from backend term payments. The modal keys drafts by payment
 * uuid, as the schedule does; the backend id is a different number and matches nothing.
 */
export const paymentDraftsFromBackend = (payments) =>
  normalizePaymentsForRow({
    payments: (payments || []).map((p) => ({ ...p, id: p.uuid ?? p.id })),
  });

// A payment adds to paid; a refund subtracts.
export const signedPaymentAmount = (amount, entryType) =>
  (isRefundEntry(entryType) ? -1 : 1) * (Number(amount) || 0);

const signedAmount = signedPaymentAmount;

/** Net verified paid from backend term payment rows (payments minus verified refunds). */
export const netVerifiedPaidFromTermPayments = (payments) =>
  (payments || [])
    .filter((p) => p.status === "VERIFIED")
    .reduce((sum, p) => sum + signedPaymentAmount(p.amount, p.entryType), 0);

/** Net pending paid from backend term payment rows. */
export const netPendingPaidFromTermPayments = (payments) =>
  (payments || [])
    .filter((p) => p.status === "PENDING_VERIFICATION")
    .reduce((sum, p) => sum + signedPaymentAmount(p.amount, p.entryType), 0);

/** Whether any payment or refund on the term is still waiting to be verified. */
export const hasPendingVerificationPayment = (payments) =>
  (payments || []).some((p) => p.status === "PENDING_VERIFICATION");

/** Sum of refund amounts still pending verification. */
export const pendingRefundTotalFromTermPayments = (payments) =>
  (payments || [])
    .filter((p) => p.status === "PENDING_VERIFICATION" && isRefundEntry(p.entryType))
    .reduce((sum, p) => sum + Math.abs(Number(p.amount) || 0), 0);

/** Verified paid minus pending refunds — max that can still be submitted/refunded. */
export const refundableAvailableFromTermPayments = (payments) => {
  const verified = Math.max(0, netVerifiedPaidFromTermPayments(payments));
  return Math.max(0, verified - pendingRefundTotalFromTermPayments(payments));
};

/** Whether a verified payment can be super-admin deleted without orphaning refunds. */
export const canDeleteVerifiedPaymentFromTermPayments = (backendPayments, excludeBackendId) => {
  const list = backendPayments || [];
  const target = list.find(
    (p) => p.id === excludeBackendId || String(p.id) === String(excludeBackendId)
  );
  if (!target || target.status !== "VERIFIED" || isRefundEntry(target.entryType)) {
    return { allowed: true };
  }

  const newNet = list
    .filter((p) => p.status === "VERIFIED" && String(p.id) !== String(excludeBackendId))
    .reduce((sum, p) => sum + signedPaymentAmount(p.amount, p.entryType), 0);

  const pendingRefunds = pendingRefundTotalFromTermPayments(list);
  const verifiedRefundTotal = list
    .filter((p) => p.status === "VERIFIED" && isRefundEntry(p.entryType))
    .reduce((sum, p) => sum + Math.abs(Number(p.amount) || 0), 0);

  if (newNet < 0) {
    return {
      allowed: false,
      reason: `Verified refunds (${formatAudCompact(
        verifiedRefundTotal
      )}) would exceed remaining payments. Delete refunds first.`,
    };
  }
  if (newNet < pendingRefunds) {
    return {
      allowed: false,
      reason: `Remaining verified paid (${formatAudCompact(
        Math.max(0, newNet)
      )}) would not cover pending refunds (${formatAudCompact(
        pendingRefunds
      )}). Delete pending refunds first.`,
    };
  }
  return { allowed: true };
};

/** Prefer live term payments over a stale imported installment paidAmount. */
export const resolveTermPaidAmountFromPayments = (payments, importedPaidAmount) => {
  const list = payments || [];
  if (list.length > 0) {
    const net = Math.max(0, netVerifiedPaidFromTermPayments(list));
    return net > 0 ? String(net) : "";
  }
  return importedPaidAmount ?? "";
};

export const sumRowPayments = (payments) =>
  (payments || []).reduce((sum, p) => sum + signedAmount(p.amount, p.entryType), 0);

export const sumPaymentsByStatus = (rowPayments, backendPayments) => {
  const statusByKey = {};
  const typeByKey = {};
  (backendPayments || []).forEach((p) => {
    if (p.uuid) {
      statusByKey[p.uuid] = p.status;
      typeByKey[p.uuid] = p.entryType;
    }
    if (p.id != null) {
      statusByKey[String(p.id)] = p.status;
      typeByKey[String(p.id)] = p.entryType;
    }
  });
  let verified = 0;
  let pending = 0;
  (rowPayments || []).forEach((p) => {
    const s = statusByKey[p.id] ?? statusByKey[String(p.id)] ?? p._localStatus ?? "NONE";
    const entryType = typeByKey[p.id] ?? typeByKey[String(p.id)] ?? p.entryType ?? "PAYMENT";
    const amt = signedAmount(p.amount, entryType);
    if (s === "VERIFIED") verified += amt;
    else if (s === "PENDING_VERIFICATION") pending += amt;
  });
  return { verified, pending };
};

export const formatAudCompact = (val) =>
  val == null
    ? "—"
    : `$${Number(val).toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

/**
 * A backend admission term "carries money" when it has recorded payments, was sent to the
 * provider, or has commission/bonus claimed. Such a term must never be deleted or have another
 * row shifted onto it by a schedule edit — that would orphan or misattribute real records.
 */
export const backendTermHasMoney = (term) =>
  Boolean(term) &&
  ((term.payments || []).length > 0 ||
    term.sentToProvider === true ||
    term.isCommissionClaimed === true ||
    term.isBonusClaimed === true);

/**
 * Bind each local schedule row to its backend admission term by a STABLE key (payment uuid),
 * never by array position. The local schedule (offer-letter JSON) is mutable — deleting or
 * reordering a row shifts every later index — while `backendTerms` is a server-cached, position
 * (termNumber)-keyed list. Joining the two by `termNumber === index + 1` silently misattributes
 * every row after a deletion. Instead:
 *
 *   1. A row that carries payment uuids binds to the backend term that owns those uuids — its
 *      identity follows the row across deletes/reorders, preserving each payment's real status.
 *   2. A row with no own payment uuids falls back to its positional term ONLY if that term has
 *      not already been claimed by a uuid match. This keeps working for backend-only payments
 *      added in another session (offer-letter JSON not yet re-saved) while refusing to bind a
 *      deleted term's payments onto the row that shifted into its slot.
 *
 * Returns Map(row.id -> backendTerm | null).
 */
export const bindRowsToBackendTerms = (termSchedule = [], backendTerms = []) => {
  const binding = new Map();
  const claimed = new Set();

  const termsByNumber = new Map();
  const termByPaymentUuid = new Map();
  (backendTerms || []).forEach((term) => {
    if (term?.termNumber != null) termsByNumber.set(term.termNumber, term);
    (term?.payments || []).forEach((p) => {
      if (p?.uuid != null) termByPaymentUuid.set(String(p.uuid), term);
    });
  });

  // Phase 1 — bind rows that carry payment uuids to the term that owns them.
  (termSchedule || []).forEach((row) => {
    const uuids = (row?.payments || []).map((p) => String(p.id));
    let match = null;
    for (const uuid of uuids) {
      const term = termByPaymentUuid.get(uuid);
      if (term && !claimed.has(term.id)) {
        match = term;
        break;
      }
    }
    if (match) {
      binding.set(row.id, match);
      claimed.add(match.id);
    }
  });

  // Phase 2 — payment-free rows fall back to their positional term only if it is unclaimed.
  (termSchedule || []).forEach((row, index) => {
    if (binding.has(row.id)) return;
    const positional = termsByNumber.get(index + 1);
    if (positional && !claimed.has(positional.id)) {
      binding.set(row.id, positional);
      claimed.add(positional.id);
    } else {
      binding.set(row.id, null);
    }
  });

  return binding;
};
